// src/components/admin/mail/useMailbox.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
    LoadStatus,
    MailFolder,
    MailFolderId,
    MailMessageDetail,
    MailMessageSummary,
} from '../../../types/mail';
import * as mailApi from './mailApi';

const PAGE_SIZE = 25;

/**
 * All folder, list and detail state for a single mail account.
 *
 * Extracted into a hook because AdminClients.tsx is 1049 lines largely from inlining exactly
 * this kind of state, and the mailbox has more of it. Same useState/useEffect/axios
 * primitives the rest of the panel uses, just not inside the component.
 */
export const useMailbox = (accountId: string | null) => {
    const [folders, setFolders] = useState<MailFolder[]>([]);
    const [folder, setFolder] = useState<MailFolderId>('inbox');

    const [messages, setMessages] = useState<MailMessageSummary[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [uidValidity, setUidValidity] = useState(0);
    const [listStatus, setListStatus] = useState<LoadStatus>('idle');

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [selectedUid, setSelectedUid] = useState<number | null>(null);
    const [detail, setDetail] = useState<MailMessageDetail | null>(null);
    const [detailStatus, setDetailStatus] = useState<LoadStatus>('idle');
    /**
     * Per-message opt-in to remote images. The reading frame's CSP needs this too — the
     * server restoring the original src is not sufficient on its own.
     */
    const [imagesAllowed, setImagesAllowed] = useState(false);

    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

    // ── the guard that matters most ──────────────────────────────────────────────
    // IMAP round trips run 0.5-3s. Switching account pills or folders quickly WILL land
    // responses out of order, and unguarded that renders one mailbox's messages under
    // another's pill — precisely the mix-up the account separation exists to prevent.
    // Every setState below is gated on still being the newest request.
    const listRequestRef = useRef(0);
    const detailRequestRef = useRef(0);

    // Debounce only the search text. AdminClients debounces pagination too, which makes
    // every page click wait 300ms for nothing.
    useEffect(() => {
        // IMAP SEARCH is slower than a DB LIKE, so wait slightly longer than the 300ms
        // used elsewhere in the panel.
        const timer = window.setTimeout(() => setDebouncedSearch(search), 350);
        return () => window.clearTimeout(timer);
    }, [search]);

    const fetchFolders = useCallback(async () => {
        if (!accountId) return;
        try {
            setFolders(await mailApi.getFolders(accountId));
        } catch {
            setFolders([]);
        }
    }, [accountId]);

    const fetchMessages = useCallback(
        async (options?: { silent?: boolean }) => {
            if (!accountId) return;

            const requestId = ++listRequestRef.current;
            if (options && options.silent) {
                setIsRefreshing(true);
            } else {
                setListStatus('loading');
            }

            try {
                const data = await mailApi.getMessages(accountId, folder, page, PAGE_SIZE, debouncedSearch);
                if (requestId !== listRequestRef.current) return; // superseded

                setMessages(data.content || []);
                setTotalPages(data.totalPages);
                setTotalElements(data.totalElements);
                setUidValidity(data.uidValidity);
                setListStatus('ready');
                setLastSyncedAt(new Date());
            } catch (err: any) {
                if (requestId !== listRequestRef.current) return;
                setListStatus('error');
                if (options && options.silent) {
                    // A background refresh must not interrupt her with a toast.
                    console.warn('Silent mail refresh failed', err);
                }
            } finally {
                if (requestId === listRequestRef.current) {
                    setIsRefreshing(false);
                }
            }
        },
        [accountId, folder, page, debouncedSearch]
    );

    // Switching account resets everything, including the folder. Without this the previous
    // mailbox's messages stay on screen under the new pill until the first response lands.
    useEffect(() => {
        listRequestRef.current++;
        detailRequestRef.current++;
        setMessages([]);
        setFolders([]);
        setFolder('inbox');
        setSearch('');
        setDebouncedSearch('');
        setPage(0);
        setTotalPages(0);
        setTotalElements(0);
        setSelectedUid(null);
        setDetail(null);
        setDetailStatus('idle');
        setListStatus(accountId ? 'loading' : 'idle');
    }, [accountId]);

    // Changing folder or search always returns to the first page and drops the open
    // message — the selected uid belongs to the folder it came from.
    useEffect(() => {
        setPage(0);
        detailRequestRef.current++;
        setSelectedUid(null);
        setDetail(null);
        setDetailStatus('idle');
    }, [folder, debouncedSearch]);

    useEffect(() => {
        fetchFolders();
    }, [fetchFolders]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    const openMessage = useCallback(
        async (uid: number) => {
            if (!accountId) return;

            const requestId = ++detailRequestRef.current;
            setSelectedUid(uid);
            setDetailStatus('loading');
            setDetail(null);
            setImagesAllowed(false); // opting in is per message, never sticky

            // Optimistic and unconditional: the dot clears immediately while the flag write
            // happens in the background. She must never wait on a flag write.
            setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, seen: true } : m)));

            try {
                const data = await mailApi.getMessage(accountId, folder, uid, uidValidity, false);
                if (requestId !== detailRequestRef.current) return;

                setDetail(data);
                setDetailStatus('ready');

                // Sent unconditionally. Setting \Seen on an already-seen message is a no-op
                // on IMAP, so there is nothing to gain by first deciding whether the message
                // was unread — and deciding it from this closure's `messages` array was
                // fragile enough that the write was being skipped entirely, with no error to
                // show for it. An idempotent request is cheaper than a condition that can be
                // silently wrong.
                mailApi.setRead(accountId, folder, uid, uidValidity, true)
                    .then(fetchFolders)
                    .catch(() => {
                        // Resync rather than guess at the previous state. No toast: a failed
                        // flag write is not something she can act on.
                        fetchMessages({ silent: true });
                        fetchFolders();
                    });
            } catch (err: any) {
                if (requestId !== detailRequestRef.current) return;
                setDetailStatus('error');

                const status = err?.response?.status;
                if (status === 404) {
                    // Moved or deleted from her phone while the list was on screen.
                    toast.error('Správa už neexistuje');
                    fetchMessages({ silent: true });
                } else if (status === 409) {
                    toast.error('Schránka sa zmenila, načítavam znova');
                    fetchMessages({ silent: true });
                }
            }
        },
        // Deliberately does NOT depend on `messages`: that made this callback change on every
        // list mutation, including its own optimistic update, for no benefit now that the
        // unread check is gone.
        [accountId, folder, uidValidity, fetchMessages, fetchFolders]
    );

    const closeMessage = useCallback(() => {
        detailRequestRef.current++;
        setSelectedUid(null);
        setDetail(null);
        setDetailStatus('idle');
        setImagesAllowed(false);
    }, []);

    /** Re-fetches the open message with remote images allowed. */
    const showRemoteImages = useCallback(async () => {
        if (!accountId || selectedUid === null) return;
        const requestId = ++detailRequestRef.current;
        try {
            const data = await mailApi.getMessage(accountId, folder, selectedUid, uidValidity, true);
            if (requestId !== detailRequestRef.current) return;
            setDetail(data);
            // Must be set alongside the refetch — the frame's CSP blocks https: otherwise,
            // and the button would appear to do nothing.
            setImagesAllowed(true);
        } catch {
            toast.error('Obrázky sa nepodarilo načítať');
        }
    }, [accountId, folder, selectedUid, uidValidity]);

    const setSeen = useCallback(
        async (uid: number, seen: boolean) => {
            if (!accountId) return;
            setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, seen } : m)));
            setDetail((prev) => (prev && prev.uid === uid ? { ...prev, seen } : prev));
            try {
                await mailApi.setRead(accountId, folder, uid, uidValidity, seen);
                fetchFolders();
            } catch {
                setMessages((prev) => prev.map((m) => (m.uid === uid ? { ...m, seen: !seen } : m)));
                toast.error('Stav správy sa nepodarilo zmeniť');
            }
        },
        [accountId, folder, uidValidity, fetchFolders]
    );

    const removeMessage = useCallback(
        async (uid: number) => {
            if (!accountId) return;
            const remaining = messages.filter((m) => m.uid !== uid);
            const permanent = folder === 'trash';
            setMessages(remaining);
            closeMessage();

            try {
                await mailApi.deleteMessage(accountId, folder, uid, uidValidity);
                toast.success(permanent ? 'Správa bola natrvalo zmazaná' : 'Správa bola presunutá do koša');
                fetchFolders();

                // Don't strand her on a page that just became empty — the bug the clients
                // table still has, where handleDelete refetches unconditionally.
                if (remaining.length === 0 && page > 0) {
                    setPage(page - 1);
                } else {
                    fetchMessages({ silent: true });
                }
            } catch (err: any) {
                toast.error(mailApi.readError(err, 'Správu sa nepodarilo zmazať'));
                fetchMessages({ silent: true });
            }
        },
        [accountId, folder, messages, uidValidity, page, closeMessage, fetchMessages, fetchFolders]
    );

    const refresh = useCallback(() => {
        fetchMessages({ silent: true });
        fetchFolders();
    }, [fetchMessages, fetchFolders]);

    /**
     * Refetch when the window regains focus, if the last sync is stale.
     *
     * Deliberately no polling interval: every poll is a live IMAP round trip, not a cached
     * read. A 60s interval would be ~1400 fetches a day per account for someone who checks
     * mail a few times a day, Gmail throttles aggressive IMAP from datacenter IPs, and a
     * poll firing exactly as the JWT expires would trip the global 401 interceptor and log
     * her out mid-read. Focus-refetch is what people actually perceive as auto-refresh.
     */
    useEffect(() => {
        const onFocus = () => {
            if (!accountId) return;
            if (!lastSyncedAt || Date.now() - lastSyncedAt.getTime() > 60000) {
                fetchMessages({ silent: true });
            }
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [accountId, lastSyncedAt, fetchMessages]);

    return {
        folders,
        folder,
        setFolder,
        messages,
        page,
        setPage,
        totalPages,
        totalElements,
        uidValidity,
        listStatus,
        search,
        setSearch,
        isSearching: debouncedSearch.length > 0,
        selectedUid,
        detail,
        detailStatus,
        imagesAllowed,
        openMessage,
        closeMessage,
        showRemoteImages,
        setSeen,
        removeMessage,
        refresh,
        isRefreshing,
        lastSyncedAt,
        pageSize: PAGE_SIZE,
    };
};
